/*******************************************************************************
 * Copyright     : Copyright (c)  2020 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc., and its use or disclosure in whole or in
 *                 part without express written permission of Honeywell Inc.
 *                 is prohibited.
 *
 * Name          : configurationSlice.ts
 *
 * Project Title : RDR7000
 *
 * Description   : Redux Slice for Configuration state
 *
 *
 * Company       : Honeywell Technology Solutions , Hyderabad
 *
 * @Author       : Amol Jagtap (H409453)
 *
 * @Date         : Aug 12, 2020
 *
 * @Version      : Version 1.0
 *
 * By            : Amol Jagtap (H409453)
 * On            : Aug 12, 2020
 * Description   : Add code to maintain state for Configuration data
 *
 *
 ******************************************************************************/

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk, RootState } from 'stores/GlobalStore';
import { apiCallBegan } from 'stores/middlewares';
import { IConfigState, IConfigData, ConfigStatusType } from 'models/configuration';

interface ISetCompletedSoldToPayload {
  soldTo: string;
}

const initialState: IConfigState = {
  completed: {
    selectedSoldTo: '',
    data: [],
    loading: false,
  },
};

const configSlice = createSlice({
  name: 'configuration',
  initialState,
  reducers: {
    setCompletedConfigData(configState: IConfigState, action: PayloadAction<IConfigData[]>) {
      configState.completed.data = action.payload;
      configState.completed.loading = false;
    },
    setCompletedSoldTo(configState: IConfigState, action: PayloadAction<ISetCompletedSoldToPayload>) {
      configState.completed.selectedSoldTo = action.payload.soldTo;
    },
    beginCompletedLoading(configState: IConfigState) {
      configState.completed.loading = true;
      configState.completed.data = [];
    },
    resetCompletedConfigDataOnError(configState: IConfigState) {
      configState.completed.data = [];
      configState.completed.loading = false;
    },
  },
});

const { setCompletedConfigData, beginCompletedLoading, resetCompletedConfigDataOnError } = configSlice.actions;

export const configReducer = configSlice.reducer;

export const fetchConfigurations = (): AppThunk => async (dispatch, getState) => {
  const soldTo = getState().customer.selectedCustomer!.soldTo;

  const url = '/configuration';
  const params = { 'sold-to': soldTo, status: ConfigStatusType.Completed };

  dispatch(
    apiCallBegan({
      url,
      params,
      onStart: beginCompletedLoading.type,
      onSuccess: setCompletedConfigData.type,
      onError: resetCompletedConfigDataOnError.type,
      showGlobalError: true,
    })
  );

  dispatch(configSlice.actions.setCompletedSoldTo({ soldTo }));
};

export const fetchAllConfigurations = (): AppThunk => async (dispatch, getState) => {
  const storeSoldTo = getState().customer.selectedCustomer;
  const soldTo = storeSoldTo === null ? getState().ui.AllAgentCustFlag : storeSoldTo.soldTo;

  const url = '/all-configuration';
  const params = { 'sold-to': soldTo, status: ConfigStatusType.Completed };

  dispatch(
    apiCallBegan({
      url,
      params,
      onStart: beginCompletedLoading.type,
      onSuccess: setCompletedConfigData.type,
      onError: resetCompletedConfigDataOnError.type,
      showGlobalError: true,
    })
  );

  dispatch(configSlice.actions.setCompletedSoldTo({ soldTo }));
};

export const selectCompletedConfig = (state: RootState) => state.configuration.completed;
